import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api";
import { hasPermission, useAuth, type RoleDefinition, type User } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { isLocationScopeAdmin } from "@/lib/location-scope";
import type { RecipeOverviewQuery, RecipeOverviewResponse } from "@shared/recipe-overview";
import type { RecipeBulkActionResponse, RecipeChanges, RecipeDraftReviewResponse } from "@shared/recipe-changes";
import { useCategories } from "@/hooks/use-data";
import { matchesRecipeDraftCategory } from "@/lib/recipe-draft-queue-filters";

export function useRecipes() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  return useQuery({ queryKey: ["recipes", workspaceId], queryFn: () => api.recipes.list(workspaceId), enabled: !!workspaceId });
}

export type RecipeOverviewFilters = RecipeOverviewQuery;

export type RecipeDraftQueueFilters = {
  categoryId?: string;
  noChangeOnly?: boolean;
  sort?: "change_desc" | "name_asc";
  page?: number;
  pageSize?: number;
};
export type RecipeIngredientReceiptResponse = {
  itemId: string;
  page: number;
  pageSize: number;
  total: number;
  costsVisible: boolean;
  rows: Array<{
    receiptId: string;
    receiptNumber: string;
    lineId?: string;
    receiptDate: string;
    supplierName: string;
    branchId: string;
    branchName: string;
    receivedQty: number | string;
    unit: string;
    costStatus: string | null;
    unitCost: number | string | null;
    lineTotal: number | string | null;
    isCurrentPriceSource?: boolean;
  }>;
};

export type RecipeIngredientReplenishmentOrderResponse = {
  itemId: string;
  page: number;
  pageSize: number;
  total: number;
  rows: Array<{
    orderId: string;
    orderNumber: string;
    orderName: string | null;
    status: string;
    createdAt: string;
    requestedDeliveryDate: string | null;
    scheduledDeliveryDate: string | null;
    itemName: string;
    quantity: number | string;
    unit: string | null;
    fromLocationName: string;
    toLocationName: string;
  }>;
};

export type RecipeRelatedDocumentSource = "receipts" | "orders";
export type RecipeRelatedDocumentResponse = {
  source: RecipeRelatedDocumentSource;
  itemId: string | null;
  page: number;
  pageSize: number;
  total: number;
  costsVisible: boolean;
  canOpenDetail: boolean;
  rows: Array<{
    documentId: string;
    documentNumber: string;
    documentDate: string;
    status: string;
    isCurrentPriceSource?: boolean;
    supplierName?: string;
    branchId?: string;
    branchName?: string;
    fromLocationId?: string;
    fromLocationName?: string;
    toLocationId?: string;
    toLocationName?: string;
    matchingItems: Array<{
      itemId: string;
      itemName: string;
      quantity: number | string;
      unit: string | null;
    }>;
  }>;
};

/**
 * Receipt confirmation/correction changes the read-only prices embedded in
 * both recipe reads. Keep this invalidation in one place so receipt screens
 * cannot refresh their own list while leaving a mounted recipe stale.
 */
export function invalidateRecipeCostQueries(queryClient: QueryClient, workspaceId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["recipes", workspaceId] }),
    queryClient.invalidateQueries({ queryKey: ["recipe-overview", workspaceId] }),
  ]);
}

/**
 * Cost fields on recipe reads depend on both the active identity and that
 * identity's role/location scope. Do not share a query result across those
 * boundaries: the QueryClient can intentionally keep entries indefinitely.
 */
export function recipeCostAccessScope(
  user: User | null,
  roles: RoleDefinition[],
) {
  const canViewCosts = hasPermission(user, "reports.cost_view", roles);
  const canViewAllCosts = hasPermission(user, "reports.cost_view_all", roles);

  return {
    userId: user?.id || "",
    roleId: user?.roleId || "",
    isAdmin: isLocationScopeAdmin(user, roles),
    canViewCosts,
    canViewAllCosts,
    managedLocationIds: Array.from(new Set((user?.managedLocationIds || []).filter(Boolean))).sort(),
  };
}

export function useRecipeOverview(filters: RecipeOverviewFilters, enabled = true) {
  const { currentWorkspace, user, roles } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const accessScope = recipeCostAccessScope(user, roles);
  return useQuery({
    queryKey: ["recipe-overview", workspaceId, filters, accessScope],
    queryFn: () => api.recipes.overview(workspaceId, filters) as Promise<RecipeOverviewResponse>,
    enabled: enabled && !!workspaceId && !!user,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export function useRecipe(id?: string | null) {
  const { currentWorkspace, user, roles } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const accessScope = recipeCostAccessScope(user, roles);
  return useQuery({
    queryKey: ["recipes", workspaceId, id, accessScope],
    queryFn: () => api.recipes.get(id!, workspaceId),
    enabled: !!workspaceId && !!id && !!user,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export function useRecipeIngredientReceipts(
  recipeId?: string | null,
  itemId?: string | null,
  page = 1,
  pageSize = 10,
  enabled = true,
) {
  const { currentWorkspace, user, roles } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const accessScope = {
    ...recipeCostAccessScope(user, roles),
    canViewReceipts: hasPermission(user, "supplier_receipts.view", roles)
      || hasPermission(user, "supplier_receipts.confirm", roles),
  };
  return useQuery<RecipeIngredientReceiptResponse>({
    queryKey: [
      "recipe-ingredient-receipts",
      workspaceId,
      recipeId || "",
      itemId || "",
      page,
      pageSize,
      accessScope,
    ],
    queryFn: () => api.recipes.ingredientReceipts(
      recipeId!,
      workspaceId,
      itemId!,
      { page, pageSize },
    ) as Promise<RecipeIngredientReceiptResponse>,
    enabled: enabled && accessScope.canViewReceipts && !!workspaceId && !!user && !!recipeId && !!itemId,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export function useRecipeIngredientReplenishmentOrders(
  recipeId?: string | null,
  itemId?: string | null,
  page = 1,
  pageSize = 10,
  enabled = true,
) {
  const { currentWorkspace, user, roles } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const accessScope = {
    userId: user?.id || "",
    roleId: user?.roleId || "",
    isAdmin: isLocationScopeAdmin(user, roles),
    managedLocationIds: Array.from(new Set((user?.managedLocationIds || []).filter(Boolean))).sort(),
    canViewOrders: hasPermission(user, "orders.view", roles)
      || hasPermission(user, "orders.view_all", roles),
  };
  return useQuery<RecipeIngredientReplenishmentOrderResponse>({
    queryKey: [
      "recipe-ingredient-replenishment-orders",
      workspaceId,
      recipeId || "",
      itemId || "",
      page,
      pageSize,
      accessScope,
    ],
    queryFn: () => api.recipes.ingredientReplenishmentOrders(
      recipeId!,
      workspaceId,
      itemId!,
      { page, pageSize },
    ) as Promise<RecipeIngredientReplenishmentOrderResponse>,
    enabled: enabled && accessScope.canViewOrders && !!workspaceId && !!user && !!recipeId && !!itemId,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export function useRecipeRelatedDocuments(
  recipeId?: string | null,
  source: RecipeRelatedDocumentSource = "receipts",
  itemId?: string | null,
  page = 1,
  pageSize = 10,
  enabled = true,
) {
  const { currentWorkspace, user, roles } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const accessScope = {
    ...recipeCostAccessScope(user, roles),
    canViewReceipts: hasPermission(user, "supplier_receipts.view", roles)
      || hasPermission(user, "supplier_receipts.confirm", roles),
    canViewOrders: hasPermission(user, "orders.view", roles)
      || hasPermission(user, "orders.view_all", roles)
      || hasPermission(user, "view_orders", roles)
      || hasPermission(user, "view_all_orders", roles),
  };
  const canViewSource = source === "receipts" ? accessScope.canViewReceipts : accessScope.canViewOrders;
  return useQuery<RecipeRelatedDocumentResponse>({
    queryKey: [
      "recipe-related-documents",
      workspaceId,
      recipeId || "",
      source,
      itemId || "",
      page,
      pageSize,
      accessScope,
    ],
    queryFn: () => api.recipes.relatedDocuments(
      recipeId!,
      workspaceId,
      { source, itemId: itemId || undefined, page, pageSize },
    ) as Promise<RecipeRelatedDocumentResponse>,
    enabled: enabled && canViewSource && !!workspaceId && !!user && !!recipeId,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export function useRecipeMutations() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const refresh = () => invalidateRecipeCostQueries(queryClient, workspaceId);
  const report = (title: string) => ({ onSuccess: () => { refresh(); toast({ title }); }, onError: (error: any) => toast({ title: "Recipe update failed", description: error.message, variant: "destructive" }) });
  return {
    create: useMutation({ mutationFn: (data: any) => api.recipes.create(workspaceId, data), ...report("Recipe saved") }),
    update: useMutation({ mutationFn: ({ id, data }: any) => api.recipes.update(id, workspaceId, data), ...report("Recipe updated") }),
    createVersion: useMutation({ mutationFn: (id: string) => api.recipes.createVersion(id, workspaceId), ...report("New recipe version created") }),
    activate: useMutation({ mutationFn: (id: string) => api.recipes.activate(id, workspaceId), ...report("Recipe activated") }),
    discard: useMutation({
      mutationFn: (id: string) => api.recipes.discard(id, workspaceId),
      onSuccess: async () => {
        await refresh();
        toast({ title: "Draft discarded", description: "The active recipe version is unchanged." });
      },
      onError: (error: any) => {
        refresh();
        toast({ title: "Could not discard draft", description: error.message, variant: "destructive" });
      },
    }),
    cancelActivation: useMutation({
      mutationFn: (id: string) => api.recipes.cancelActivation(id, workspaceId),
      onSuccess: async () => {
        await refresh();
        toast({ title: "Recipe activation cancelled", description: "The version is archived. History, product, and stock are unchanged." });
      },
      onError: (error: any) => {
        refresh();
        toast({ title: "Could not cancel activation", description: error.message, variant: "destructive" });
      },
    }),
  };
}

export type RecipeDraftQueueResponse = {
  rows: RecipeDraftQueueRow[];
  page: number;
  pageSize: number;
  total: number;
  noChangeTotal: number;
};

export function useRecipeDraftQueue(filters: RecipeDraftQueueFilters, enabled = true) {
  const { data: categories = [] } = useCategories("finished_product");
  const review = useRecipeDraftReview(enabled);
  const data = useMemo<RecipeDraftQueueResponse | undefined>(() => {
    if (!review.data) return undefined;
    const filtered = review.data.drafts.filter((row) =>
      matchesRecipeDraftCategory(row, filters.categoryId, categories as any[])
      && (!filters.noChangeOnly || row.changes.identical)
    );
    filtered.sort((a, b) => filters.sort === "name_asc"
      ? a.productName.localeCompare(b.productName)
      : changeSize(b.changes) - changeSize(a.changes) || a.productName.localeCompare(b.productName));
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 25;
    return {
      rows: filtered.slice((page - 1) * pageSize, page * pageSize).map((row) => ({
        ...row,
        categoryId: row.productCategory,
        createdByName: row.createdByName || "—",
      })),
      page,
      pageSize,
      total: filtered.length,
      noChangeTotal: review.data.drafts.filter((row) =>
        matchesRecipeDraftCategory(row, filters.categoryId, categories as any[]) && row.changes.identical
      ).length,
    };
  }, [review.data, filters, categories]);
  return { ...review, data };
}

export function useRecipeDraftQueueMutations() {
  const { currentWorkspace, user } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const userId = user?.id || "";
  const queryClient = useQueryClient();
  const reconcile = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["recipe-draft-queue", workspaceId, userId] }),
      queryClient.invalidateQueries({ queryKey: ["recipe-draft-review", workspaceId, userId] }),
      queryClient.invalidateQueries({ queryKey: ["recipes", workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ["recipe-overview", workspaceId] }),
    ]);
  };
  const options = (mutationFn: (ids: string[]) => Promise<unknown>) => useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await mutationFn(ids) as RecipeBulkActionResponse;
      const outcomes = response.outcomes.map((outcome) => ({
        recipeId: outcome.recipeId,
        productName: outcome.productName || outcome.recipeId,
        success: outcome.success,
        error: outcome.message,
      }));
      return {
        succeeded: outcomes.filter((outcome) => outcome.success),
        failed: outcomes.filter((outcome) => !outcome.success),
      };
    },
    onSettled: reconcile,
    retry: false,
  });
  return {
    activate: options((ids) => api.recipes.activateDrafts(workspaceId, ids)),
    discard: options((ids) => api.recipes.discardDrafts(workspaceId, ids)),
    reconcile,
  };
}

export type RecipeDraftQueueRow = {
  id: string;
  productItemId: string;
  productName: string;
  productNameEn?: string | null;
  productSku: string;
  categoryId?: string | null;
  categoryName?: string | null;
  version: number;
  createdAt: string;
  createdByName: string;
  changes: RecipeChanges;
};

export type RecipeDraftQueueOutcome = {
  recipeId: string;
  productName: string;
  success: boolean;
  error?: string | null;
};

function changeSize(changes: RecipeChanges) {
  const { added, removed, changed } = changes.counts;
  return added + removed + changed + (changes.yieldChanged ? 1 : 0);
}

export type RecipeDraftQueueMutationResult = {
  succeeded: RecipeDraftQueueOutcome[];
  failed: RecipeDraftQueueOutcome[];
};

export function useRecipeDraftReview(enabled = true) {
  const { currentWorkspace, user } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const userId = user?.id || "";
  return useQuery<RecipeDraftReviewResponse>({
    queryKey: ["recipe-draft-review", workspaceId, userId],
    queryFn: () => api.recipes.draftQueue(workspaceId) as Promise<RecipeDraftReviewResponse>,
    enabled: enabled && !!workspaceId && !!userId,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}
